import { useEffect, useState } from "react";

interface UseCountdownReturn {
  remainingSeconds: number | null;
  formattedTime: string;
  isExpired: boolean;
  isActive: boolean;
}

function calculateRemaining(expiresAt: string | null): number | null {
  if (!expiresAt) {
    return null;
  }

  const endTime = new Date(expiresAt).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((endTime - now) / 1000));
}

export function useCountdown(expiresAt: string | null): UseCountdownReturn {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(() =>
    calculateRemaining(expiresAt),
  );

  useEffect(() => {
    if (!expiresAt) {
      return;
    }

    const tick = () => {
      const remaining = calculateRemaining(expiresAt);
      setRemainingSeconds(remaining);
      return remaining;
    };

    // Initial tick to sync state after timestamp changes
    const initialRemaining = tick();
    if (initialRemaining === 0) {
      return;
    }

    const interval = setInterval(() => {
      const remaining = tick();
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const isExpired = remainingSeconds === 0;
  const isActive = remainingSeconds !== null && remainingSeconds > 0;

  const formattedTime =
    remainingSeconds !== null
      ? `${String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:${String(remainingSeconds % 60).padStart(2, "0")}`
      : "--:--";

  return {
    remainingSeconds,
    formattedTime,
    isExpired,
    isActive,
  };
}
